import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  RichTextContent,
  useCollectionBinding,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Amenity {
  title?: string | null;
  description?: string | null;
  group?: string | null;
  amenity_image?: string | null;
  amenity_image_alt_text?: string | null;
}

interface GalleryPhoto {
  image?: string | null;
  original_url?: string | null;
  alt_text?: string | null;
  title?: string | null;
}

/**
 * Absent amenity descriptions can come back as the literal `false` (boolean)
 * or the string `"false"` rather than null/undefined, so normalize those away
 * before using the value — otherwise `.trim()` throws on a boolean and the
 * literal text "false" would render on screen.
 */
function amenityDescription(item: Amenity): string {
  if (typeof item.description !== 'string') return '';
  const trimmed = item.description.trim();
  return trimmed.toLowerCase() === 'false' ? '' : trimmed;
}

/** Prefer the amenity's description; fall back to its title when absent. */
function amenityLabel(item: Amenity): string {
  return amenityDescription(item) || item.title?.trim() || '';
}

// ─── Camera icon ──────────────────────────────────────────────────────────────

function CameraIcon({ className = '' }: { className?: string }) {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}

// ─── Full-width carousel with bottom-right arrow nav ─────────────────────────

function FullWidthCarousel({ photos }: { photos: GalleryPhoto[] }) {
  const [current, setCurrent] = useState(0);
  const [transitioning, setTransitioning] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const count = photos.length;

  const goTo = useCallback(
    (idx: number) => {
      if (transitioning || count <= 1) return;
      setTransitioning(true);
      setCurrent(idx);
      setTimeout(() => setTransitioning(false), 600);
    },
    [transitioning, count],
  );

  const next = useCallback(() => goTo((current + 1) % count), [current, count, goTo]);
  const prev = useCallback(() => goTo((current - 1 + count) % count), [current, count, goTo]);

  useEffect(() => {
    if (count <= 1) return;
    timerRef.current = setTimeout(next, 5000);
    return () => clearTimeout(timerRef.current);
  }, [current, count, next]);

  if (!count) return null;

  return (
    <div className="relative w-full h-[55vh] md:h-[68vh] min-h-[360px] max-h-[720px] overflow-hidden">
      {photos.map((photo, i) => (
        <VibesitesImage
          key={i}
          url={photo.image ?? photo.original_url ?? ''}
          alt={photo.alt_text ?? photo.title ?? ''}
          priority={i === 0}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[600ms] ease-in-out ${
            i === current ? 'opacity-100' : 'opacity-0'
          }`}
        />
      ))}

      {count > 1 && (
        <div className="absolute bottom-6 right-6 z-10 flex items-center gap-3">
          <button
            type="button"
            onClick={prev}
            aria-label="Previous photo"
            className="w-11 h-11 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/35 text-white border border-white/70 backdrop-blur-sm transition-all duration-200"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
            </svg>
          </button>
          <button
            type="button"
            onClick={next}
            aria-label="Next photo"
            className="w-11 h-11 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/35 text-white border border-white/70 backdrop-blur-sm transition-all duration-200"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Apartment section (sticky photo + centered heading) ─────────────────────

interface GroupSectionProps {
  headingPath: string;
  headingDefaultValue: string;
  subtitlePath?: string;
  subtitleDefaultValue?: string;
  items: Amenity[];
}

// The "In Your Home" feature image is its own tokenized slot group so editors
// can swap it and the field generator can auto-select a strong interior photo.
// It's the base image for the sticky panel; clicking an amenity that has its own
// photo overrides it interactively. Bound to photo_gallery (image + alt) with a
// generator that picks a high-quality interior / in-home-amenity shot.
const inHomeImageDef: SlotGroupDefinition = {
  label: 'In-Home Feature Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[0].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[0].alt_text',
      defaultValue: 'Apartment interior',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: {
      image: 'image',
      alt: 'alt_text',
    },
  },
  generator: {
    prompt:
      'Pick a single high-quality interior photo that best showcases the apartment interior and/or an in-home amenity — kitchen, living space, finishes, fixtures, or appliances. Avoid exterior, community-area, and amenity-building shots.',
    context:
      'The sticky feature image beside the "In Your Home" apartment amenity list; must depict the apartment interior.',
    sourceCollection: 'photo_gallery',
  },
};

function ApartmentSection({
  headingPath,
  headingDefaultValue,
  subtitlePath,
  subtitleDefaultValue,
  items,
}: GroupSectionProps) {
  const inHomeImage = useSlotGroup('amenitieslist.in_home_image', inHomeImageDef);

  const itemsWithImages = useMemo(
    () => items.filter((a) => a.amenity_image),
    [items],
  );

  const [activeIdx, setActiveIdx] = useState<number | null>(
    itemsWithImages.length > 0 ? 0 : null,
  );

  // Reset active index when items change
  useEffect(() => {
    setActiveIdx(itemsWithImages.length > 0 ? 0 : null);
  }, [itemsWithImages.length]);

  const activePhoto = activeIdx !== null ? itemsWithImages[activeIdx] : null;

  // Prefer the selected amenity's own photo; otherwise show the tokenized
  // in-home feature image so the panel is never an empty box.
  const displayImageUrl = activePhoto?.amenity_image ?? inHomeImage.image ?? '';
  const displayImageAlt =
    activePhoto?.amenity_image_alt_text ??
    activePhoto?.title ??
    inHomeImage.alt ??
    '';

  if (items.length === 0) return null;

  return (
    <div className="bg-white py-16 md:py-24">
      {/* Category heading + descriptive subtitle — aligned with the right column on md+ */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8 md:mb-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16">
          <div className="hidden md:block" aria-hidden="true" />
          <div>
            <RichTextContent
              path={headingPath}
              label="Category Heading"
              defaultValue={`<p>${headingDefaultValue}</p>`}
              as="h2"
              className="font-heading text-4xl sm:text-5xl md:text-6xl font-normal text-[var(--vibe-color-text)] leading-[1.1] text-left mb-4"
            />
            {subtitlePath && (
              <RichTextContent
                path={subtitlePath}
                label="Category Subtitle"
                defaultValue={`<p>${subtitleDefaultValue || ''}</p>`}
                as="div"
                className="font-body text-base text-[var(--vibe-color-text-muted)] leading-relaxed text-left pb-6 border-b border-[var(--vibe-color-border)]"
              />
            )}
          </div>
        </div>
      </div>

      {/* Two-column layout */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16 items-start">

        {/* Left: sticky photo — tokenized in-home feature image slot group */}
        <EditableSlotGroup
          groupPath="amenitieslist.in_home_image"
          definition={inHomeImageDef}
          className="md:sticky md:top-24 self-start"
        >
          {displayImageUrl ? (
            <VibesitesImage
              key={displayImageUrl}
              url={displayImageUrl}
              alt={displayImageAlt}
              className="w-full rounded-lg object-cover transition-opacity duration-300"
              style={{ aspectRatio: '4/5' }}
            />
          ) : (
            <div
              className="w-full rounded-lg bg-[var(--vibe-color-surface)]"
              style={{ aspectRatio: '4/5' }}
            />
          )}
        </EditableSlotGroup>

        {/* Right: list with dividers */}
        <ul className="divide-y divide-[var(--vibe-color-border)] border-t border-b border-[var(--vibe-color-border)]">
          {items.map((item, i) => {
            const photoIdx = item.amenity_image
              ? itemsWithImages.findIndex((p) => p === item)
              : -1;
            const isActive = photoIdx !== -1 && photoIdx === activeIdx;
            const hasPhoto = photoIdx !== -1;

            return (
              <li key={i}>
                {hasPhoto ? (
                  <button
                    type="button"
                    onClick={() => setActiveIdx(photoIdx)}
                    className={`w-full py-6 flex items-center justify-between text-left group transition-colors ${
                      isActive
                        ? 'text-[var(--vibe-color-secondary)]'
                        : 'text-[var(--vibe-color-text)] hover:text-[var(--vibe-color-secondary)]'
                    }`}
                  >
                    <span className="font-body text-lg md:text-xl leading-snug pr-4">
                      {amenityLabel(item)}
                    </span>
                    <CameraIcon
                      className={`flex-shrink-0 transition-colors ${
                        isActive
                          ? 'text-[var(--vibe-color-secondary)]'
                          : 'text-[var(--vibe-color-text-muted)] group-hover:text-[var(--vibe-color-secondary)]'
                      }`}
                    />
                  </button>
                ) : (
                  <div className="py-6">
                    <span className="font-body text-lg md:text-xl leading-snug text-[var(--vibe-color-text)]">
                      {amenityLabel(item)}
                    </span>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

// ─── Community section (dark bg + 2-column grid + lightbox) ──────────────────

function CommunitySection({ headingPath, headingDefaultValue, items }: GroupSectionProps) {
  const [lightboxItem, setLightboxItem] = useState<Amenity | null>(null);

  // Close lightbox on Escape
  useEffect(() => {
    if (!lightboxItem) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxItem(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxItem]);

  if (items.length === 0) return null;

  return (
    <>
      <div className="bg-primary py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Left-aligned heading */}
          <div className="mb-10 md:mb-14">
            <RichTextContent
              path={headingPath}
              label="Category Heading"
              defaultValue={`<p>${headingDefaultValue}</p>`}
              as="h2"
              className="font-heading text-4xl sm:text-5xl md:text-6xl font-normal text-primary-foreground leading-[1.1]"
            />
          </div>

          {/* Two-column grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 md:gap-x-12 lg:gap-x-20">
            {items.map((item, i) => {
              const hasPhoto = !!item.amenity_image;
              return (
                <div key={i} className="border-b border-primary-foreground/15">
                  {hasPhoto ? (
                    <button
                      type="button"
                      onClick={() => setLightboxItem(item)}
                      className="w-full py-5 flex items-center justify-between text-left gap-4 group transition-colors hover:bg-primary-foreground/[0.03]"
                      aria-label={`View photo of ${item.title || 'amenity'}`}
                    >
                      <span className="font-body text-base md:text-lg text-primary-foreground leading-snug">
                        {amenityLabel(item)}
                      </span>
                      <CameraIcon className="text-primary-foreground/80 group-hover:text-primary-foreground flex-shrink-0 transition-colors" />
                    </button>
                  ) : (
                    <div className="py-5">
                      <span className="font-body text-base md:text-lg text-primary-foreground leading-snug">
                        {amenityLabel(item)}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Lightbox modal */}
      {lightboxItem && lightboxItem.amenity_image && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8 cursor-zoom-out"
          onClick={() => setLightboxItem(null)}
          role="dialog"
          aria-modal="true"
          aria-label={`Photo of ${lightboxItem.title || 'amenity'}`}
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setLightboxItem(null);
            }}
            aria-label="Close photo"
            className="absolute top-6 right-6 w-11 h-11 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/30 text-white border border-white/40 transition-all duration-200 cursor-pointer"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <VibesitesImage
            url={lightboxItem.amenity_image}
            alt={lightboxItem.amenity_image_alt_text ?? lightboxItem.title ?? ''}
            className="max-w-full max-h-[85vh] object-contain rounded-lg"
          />

          {lightboxItem.title && (
            <p className="absolute bottom-6 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-black/60 text-white text-sm font-medium font-body whitespace-nowrap">
              {lightboxItem.title}
            </p>
          )}
        </div>
      )}
    </>
  );
}

// ─── Featured amenity spotlight (large image + eyebrow + title + description) ──

// Single slot group for the whole spotlight: the image, title and alt text are
// bound to one `amenities` item via `sourceBinding`, while the eyebrow and
// description are free fields. The group-level generator picks the single most
// standout amenity (freezing image/title/alt from it), and the description
// field generator writes the spotlight copy for that same amenity. Keeping it
// all in one group means one click edits the whole feature and one generation
// run keeps every field describing the same amenity.
const featuredAmenityDef: SlotGroupDefinition = {
  label: 'Featured Amenity',
  fields: {
    eyebrow: {
      label: 'Eyebrow',
      type: 'text',
      defaultValue: 'Featured Amenity',
    },
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'amenities[0].amenity_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'amenities[0].amenity_image_alt_text',
      defaultValue: 'Resort-style pool',
    },
    title: {
      label: 'Title',
      type: 'text',
      defaultField: 'amenities[0].title',
      defaultValue: 'Resort-Style Pool',
    },
    description: {
      label: 'Description',
      type: 'richtext',
      defaultField: 'amenities[0].description',
      defaultValue:
        '<p>A resident favorite — thoughtfully designed and ready for you to enjoy every day.</p>',
      generator: {
        prompt:
          'Write a short spotlight section on the standout amenity selected for this card — what it is and why residents value it.',
        context:
          'Sits beside the featured amenity image at the top of the Amenities page; must describe the same amenity chosen for this group.',
        length: '2–3 sentences',
      },
    },
  },
  sourceBinding: {
    collection: 'amenities',
    defaultIndex: 0,
    fieldMappings: {
      image: 'amenity_image',
      alt: 'amenity_image_alt_text',
      title: 'title',
    },
  },
  generator: {
    prompt:
      'Pick the single most standout, high-impact amenity at this property and pair it with a real photo. If the amenity has no image of its own, choose a photo_gallery image that genuinely depicts it.',
    context:
      'The featured-amenity spotlight at the very top of the Amenities page — one amenity only; the image, title, and description all describe this same amenity.',
    sourceCollection: 'amenities',
  },
};

function FeaturedAmenitySection() {
  const featured = useSlotGroup('amenitieslist.featured', featuredAmenityDef);

  return (
    <div className="bg-white py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <EditableSlotGroup
          groupPath="amenitieslist.featured"
          definition={featuredAmenityDef}
          className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16 items-center"
        >
          {/* Image */}
          <div>
            {featured.image ? (
              <VibesitesImage
                url={featured.image}
                alt={featured.alt ?? featured.title ?? ''}
                priority
                className="w-full rounded-2xl object-cover shadow-lg"
                style={{ aspectRatio: '4/3' }}
              />
            ) : (
              <div
                className="w-full rounded-2xl bg-[var(--vibe-color-surface)]"
                style={{ aspectRatio: '4/3' }}
              />
            )}
          </div>

          {/* Content */}
          <div>
            {featured.eyebrow && (
              <p className="font-body text-sm font-semibold uppercase tracking-[0.2em] text-[var(--vibe-color-secondary)] mb-4">
                {featured.eyebrow}
              </p>
            )}

            {featured.title && (
              <h2 className="font-heading text-4xl sm:text-5xl md:text-6xl font-normal text-[var(--vibe-color-text)] leading-[1.1] mb-5">
                {featured.title}
              </h2>
            )}

            {featured.description && (
              <div
                className="font-body text-lg text-[var(--vibe-color-text-muted)] leading-relaxed"
                dangerouslySetInnerHTML={{ __html: featured.description }}
              />
            )}
          </div>
        </EditableSlotGroup>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function AmenitiesList() {
  const apartmentItems = useCollectionBinding<Amenity>('amenitieslist.apartment_items', {
    collection: 'amenities',
    filter: { group: 'Apartment' },
  });

  const communityItems = useCollectionBinding<Amenity>('amenitieslist.community_items', {
    collection: 'amenities',
    filter: { group: 'Community' },
  });

  const carouselPhotos = useCollectionBinding<GalleryPhoto>('amenitieslist.carousel_photos', {
    collection: 'photo_gallery',
    defaultLimit: 8,
  });

  const usableCarouselPhotos = useMemo(
    () => carouselPhotos.filter((p) => p.image ?? p.original_url),
    [carouselPhotos],
  );

  const hasContent = apartmentItems.length > 0 || communityItems.length > 0;

  return (
    <section>
      {hasContent ? (
        <>
          <FeaturedAmenitySection />

          <ApartmentSection
            headingPath="amenitieslist.apartment_heading"
            headingDefaultValue="In Your Home"
            subtitlePath="amenitieslist.apartment_subtitle"
            subtitleDefaultValue="Step inside thoughtfully designed apartments where every detail elevates your everyday."
            items={apartmentItems}
          />

          {/* Full-width photo carousel — sits between the two group sections */}
          {usableCarouselPhotos.length > 0 && communityItems.length > 0 && (
            <div className="w-full">
              <FullWidthCarousel photos={usableCarouselPhotos} />
            </div>
          )}

          <CommunitySection
            headingPath="amenitieslist.community_heading"
            headingDefaultValue="In Your Community"
            items={communityItems}
          />
        </>
      ) : (
        <div className="bg-white py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-[var(--vibe-color-text-muted)] py-12">
            <p className="font-body text-lg">Amenities will be displayed here.</p>
          </div>
        </div>
      )}

      {/* ─── Hidden schema-lint anchors ─────────────────────────────────────── */}
      {/* The visible headings/subtitles render via child sections that receive   */}
      {/* their path as a prop, so the source-level linter can't see the literal  */}
      {/* token paths. These never render visibly; they exist solely so the       */}
      {/* linter finds the canonical literal path for every richtext token.       */}
      <RichTextContent
        path="amenitieslist.apartment_heading"
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <RichTextContent
        path="amenitieslist.apartment_subtitle"
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <RichTextContent
        path="amenitieslist.community_heading"
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
    </section>
  );
}

// ─── Content Schema ───────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'AmenitiesList',
  displayName: 'Amenities List',
  tokens: {
    'amenitieslist.apartment_heading': {
      label: 'Apartment Section Heading',
      type: 'richtext',
      defaultValue: '<p>In Your Home</p>',
    },
    'amenitieslist.apartment_subtitle': {
      label: 'Apartment Section Subtitle',
      type: 'richtext',
      defaultValue:
        '<p>Step inside thoughtfully designed apartments where every detail elevates your everyday.</p>',
    },
    'amenitieslist.community_heading': {
      label: 'Community Section Heading',
      type: 'richtext',
      defaultValue: '<p>In Your Community</p>',
    },
  },
  slotGroups: {
    'amenitieslist.featured': featuredAmenityDef,
    'amenitieslist.in_home_image': inHomeImageDef,
  },
  collectionBindings: {
    'amenitieslist.apartment_items': {
      label: 'Apartment Amenities',
      collection: 'amenities',
    },
    'amenitieslist.community_items': {
      label: 'Community Amenities',
      collection: 'amenities',
    },
    'amenitieslist.carousel_photos': {
      label: 'Between-Sections Carousel Photos',
      description: 'Full-width photo carousel shown between the apartment and community sections.',
      collection: 'photo_gallery',
      defaultLimit: 8,
    },
  },
};

(AmenitiesList as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default AmenitiesList;
